// SPDX-License-Identifier: GPL-3.0-or-later
// Small DOM seam of the client shell.
// Every view writes text through these interfaces. A real HTMLElement fits them.
// A test passes a plain object. No view writes HTML. Data goes in as text only.

/** A node that shows text. Always set `textContent`, never HTML. */
export interface TextSlot {
  textContent: string | null;
  hidden: boolean;
  setAttribute(name: string, value: string): void;
}

/** A node that draws a bar. The width comes from a CSS custom property, in percent. */
export interface BarSlot extends TextSlot {
  readonly style: { setProperty(name: string, value: string): void };
}

/** A list node. The view fills it with one text item for each line. */
export interface ListSlot {
  readonly ownerDocument: { createElement(tag: string): TextSlot };
  replaceChildren(...nodes: TextSlot[]): void;
}

/** View a real element as a list slot. The element must be a `ul` or an `ol`. */
export function asList(el: Element): ListSlot {
  return el as unknown as ListSlot;
}

/** View a real element as a text slot. */
export function asText(el: Element): TextSlot {
  return el as unknown as TextSlot;
}

/** View a real element as a bar slot. */
export function asBar(el: Element): BarSlot {
  return el as unknown as BarSlot;
}

/** Fill a list with one `li` for each line. The text goes in with `textContent`. */
export function fillList(list: ListSlot, lines: readonly string[]): void {
  const items = lines.map((line) => {
    const li = list.ownerDocument.createElement("li");
    li.textContent = line;
    return li;
  });
  list.replaceChildren(...items);
}

/** Set a bar to a fraction between 0 and 1. A bad value gives an empty bar. */
export function setBar(bar: BarSlot, fraction: number): void {
  const f = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  const percent = Math.round(f * 100);
  bar.style.setProperty("--bar", `${percent}%`);
  bar.setAttribute("aria-valuenow", String(percent));
}

/** Find the element with `data-slot="<name>"`. A missing slot is a bug in the page: throw. */
export function slot<T extends Element = HTMLElement>(root: ParentNode, name: string): T {
  const found = root.querySelector<T>(`[data-slot="${name}"]`);
  if (found === null) throw new Error(`Missing page slot: ${name}`);
  return found;
}

/** Cut a string to `max` code points and add an ellipsis. Used for names on the overlay. */
export function ellipsize(text: string, max: number): string {
  const chars = Array.from(text);
  return chars.length <= max ? text : `${chars.slice(0, Math.max(0, max - 1)).join("")}…`;
}
